import React, { useState, useEffect } from 'react';
import { useAdmin } from './AdminContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Building2, Edit, Ban, RotateCcw, X } from 'lucide-react';

const EMPTY = { company: '', contact_name: '', phone: '', email: '', billing_address: '', payment_terms: '', notes: '' };

export default function AdminClients() {
  const { authFetch, canWrite, isAdmin } = useAdmin();
  const [params] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const q = new URLSearchParams();
      if (statusFilter) q.set('status', statusFilter);
      if (search) q.set('search', search);
      const res = await authFetch(`/api/admin/clients?${q}`);
      if (res.ok) setClients(await res.json());
    } catch {}
  };

  useEffect(() => { load(); }, [statusFilter]);
  useEffect(() => { if (params.get('action') === 'new' && canWrite()) openNew(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ company: c.company, contact_name: c.contact_name, phone: c.phone, email: c.email, billing_address: c.billing_address, payment_terms: c.payment_terms, notes: c.notes }); setModalOpen(true); };

  const save = async () => {
    if (!form.company.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `/api/admin/clients/${editing.id}` : '/api/admin/clients';
      const res = await authFetch(url, { method, body: JSON.stringify(form) });
      if (res.ok) { toast.success(editing ? 'Client updated' : 'Client created'); setModalOpen(false); load(); }
      else { const e = await res.json(); toast.error(e.detail || 'Error'); }
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const toggleStatus = async (c) => {
    const newStatus = c.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await authFetch(`/api/admin/clients/${c.id}`, { method: 'PUT', body: JSON.stringify({ status: newStatus }) });
      if (res.ok) { toast.success(`Client ${newStatus === 'active' ? 'reactivated' : 'deactivated'}`); load(); }
    } catch {}
  };

  const doSearch = () => load();

  return (
    <div data-testid="admin-clients">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold" style={{ color: '#1B2A6B', fontFamily: 'Chivo, sans-serif' }}>Clients</h1>
        {canWrite() && <Button onClick={openNew} className="gap-2 font-semibold" style={{ background: '#B8962E', color: '#fff' }} data-testid="add-client-btn"><Plus className="w-4 h-4" /> Add Client</Button>}
      </div>

      <Card className="border border-gray-200 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch()} placeholder="Search clients..." className="pl-10" data-testid="client-search" />
            </div>
            <div className="flex gap-2">
              {['active', 'inactive', ''].map(s => (
                <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm" onClick={() => setStatusFilter(s)}
                  style={statusFilter === s ? { background: '#1B2A6B', color: '#fff' } : {}}
                  data-testid={`filter-${s || 'all'}`}>
                  {s === '' ? 'All' : s === 'active' ? 'Active' : 'Inactive'}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {clients.length === 0 && <p className="text-center text-gray-400 py-12">No clients found</p>}
        {clients.map(c => (
          <Card key={c.id} className="border border-gray-200 hover:shadow-sm transition-shadow" data-testid={`client-${c.id}`}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#1B2A6B15' }}>
                    <Building2 className="w-5 h-5" style={{ color: '#1B2A6B' }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold" style={{ color: '#1B2A6B' }}>{c.company}</span>
                      <Badge variant={c.status === 'active' ? 'default' : 'secondary'} className="text-xs" style={c.status === 'active' ? { background: '#10B98122', color: '#10B981' } : {}}>{c.status}</Badge>
                    </div>
                    <p className="text-sm text-gray-500">{c.contact_name}{c.phone ? ` • ${c.phone}` : ''}{c.email ? ` • ${c.email}` : ''}</p>
                    {c.payment_terms && <p className="text-xs text-gray-400 mt-1">Terms: {c.payment_terms}</p>}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {canWrite() && <Button variant="outline" size="sm" onClick={() => openEdit(c)} className="gap-1" data-testid={`edit-${c.id}`}><Edit className="w-3.5 h-3.5" /> Edit</Button>}
                  {isAdmin() && (
                    <Button variant="outline" size="sm" onClick={() => toggleStatus(c)} className={`gap-1 ${c.status === 'active' ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-green-600 border-green-200 hover:bg-green-50'}`}
                      data-testid={`toggle-${c.id}`}>
                      {c.status === 'active' ? <><Ban className="w-3.5 h-3.5" /> Deactivate</> : <><RotateCcw className="w-3.5 h-3.5" /> Reactivate</>}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg" data-testid="client-modal">
          <DialogHeader>
            <DialogTitle style={{ color: '#1B2A6B', fontFamily: 'Chivo, sans-serif' }}>{editing ? 'Edit Client' : 'New Client'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5"><Label className="text-sm font-semibold">Company *</Label><Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} data-testid="form-company" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Contact Name</Label><Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} data-testid="form-contact-name" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="form-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="form-email" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Payment Terms</Label><Input value={form.payment_terms} onChange={e => setForm({...form, payment_terms: e.target.value})} placeholder="e.g. Net 30" data-testid="form-payment-terms" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-sm font-semibold">Billing Address</Label><Textarea value={form.billing_address} onChange={e => setForm({...form, billing_address: e.target.value})} rows={2} data-testid="form-billing" /></div>
            <div className="space-y-1.5"><Label className="text-sm font-semibold">Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} data-testid="form-notes" /></div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} style={{ background: '#B8962E', color: '#fff' }} data-testid="form-save">{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
